import * as React from 'react';

import { cn } from '@/lib/utils';

// Themed text input — the canonical single-line field for the app. Matches
// the DatePicker trigger and Radix Select trigger idiom (44px touch target,
// rounded-lg, --color-* token family) so forms read as one consistent set of
// controls rather than a mix of raw <input>s with ad-hoc inline styles.
//
// NOTE: distinct from the shadcn-token `Input` in ./input.tsx, which uses the
// `border-input`/`bg-transparent`/`ring-ring` family the rest of this app does
// NOT theme. Prefer this component for product forms.
const TextInput = React.forwardRef<HTMLInputElement, React.ComponentProps<'input'>>(
  ({ className, style, ...props }, ref) => (
    <input
      ref={ref}
      className={cn(
        // 16px on phones, 14px from sm up. iOS Safari force-zooms the
        // page whenever a focused field's font-size is under 16px — that
        // is the "base zoom goes mad" on every mobile form. Desktop keeps
        // the tighter 14px.
        'w-full min-h-[44px] px-3 rounded-lg border text-base sm:text-sm transition-colors',
        'focus:outline-none focus:ring-2 focus:ring-[var(--color-primary-500)] focus:border-transparent',
        'placeholder:text-[var(--color-text-tertiary)] disabled:opacity-60 disabled:cursor-not-allowed',
        className,
      )}
      // Merge so a caller passing an `undefined` style value (e.g. a
      // conditional `borderColor: error ? x : undefined`) can't clobber our
      // themed defaults — only defined overrides win.
      style={{
        borderColor: 'var(--color-border-primary)',
        backgroundColor: 'var(--color-bg-primary)',
        color: 'var(--color-text-primary)',
        ...Object.fromEntries(
          Object.entries(style ?? {}).filter(([, v]) => v !== undefined),
        ),
      }}
      {...props}
    />
  ),
);
TextInput.displayName = 'TextInput';

export { TextInput };
